import {
  legacyCallbackGuestParser,
  legacyCallbackSummaryParser,
  partnerReturnMethodParser,
  partnerReturnUrlParser,
} from "../../common/jsonRpcTypes";
import type { Booking, LegacyCallbackGuest, LegacyCallbackSummary } from "../../common/jsonRpcTypes";
import type { BookingConfirmationData } from "../components/bookingForm/BookingForm";

type ReturnRedirectBookingType = NonNullable<LegacyCallbackSummary["type"]>;

/**
 * Booking data sent to a partner `return_url`.
 *
 * Carries every field legacy DMN `/book` sent (its booking summary and
 * deposits, with `status`, `auto_confirmed`, `date` and `duration` set as DMN
 * set them) alongside Guest Checkout's own keys, so integrations built against
 * either keep working. It holds no personal details, so it is safe to put on a
 * GET query string.
 */
export interface ReturnRedirectPayload extends Omit<LegacyCallbackSummary, "type"> {
  _id: string;
  booking_id: string;
  reference: string;
  venue_id: string;
  date: string;
  time: string;
  num_people: number;
  duration?: number;
  type: ReturnRedirectBookingType;
  type_id: string;
  type_name?: string;
  status?: string;
  booking_status?: string;
  auto_confirmed?: boolean;
  venue_title: string;
}

/** The POST body's `booking` JSON: the payload plus the guest details a POST may carry. */
export interface ReturnRedirectPostBooking extends ReturnRedirectPayload, LegacyCallbackGuest {
  bookingStatus?: string;
  venue?: BookingConfirmationData["venue"];
}

export interface ReturnRedirectData {
  payload: ReturnRedirectPayload;
  postBooking: ReturnRedirectPostBooking;
}

interface ReturnRedirectInput {
  bookingId: string;
  reference: string;
  venueId: string;
  date: string;
  time: string;
  numPeople: number;
  duration?: number;
  typeId: string;
  typeName?: string;
  status?: string;
  venueTitle: string;
}

const ISO_DATE_PREFIX = /^\d{4}-\d{2}-\d{2}/;

function toDateOnly(date: string): string {
  return ISO_DATE_PREFIX.exec(date)?.[0] ?? date;
}

/** The non-personal legacy fields of a booking, or none when it is unreadable. */
function legacySummaryOf(
  booking: BookingConfirmationData["booking"] | LegacyCallbackSummary | undefined,
): LegacyCallbackSummary {
  const parsed = legacyCallbackSummaryParser.safeParse(booking);
  return parsed.success ? parsed.data : {};
}

function buildPayload(legacy: LegacyCallbackSummary, input: ReturnRedirectInput): ReturnRedirectPayload {
  return {
    ...legacy,
    _id: input.bookingId,
    booking_id: input.bookingId,
    reference: input.reference,
    venue_id: input.venueId,
    date: toDateOnly(input.date),
    time: input.time,
    num_people: input.numPeople,
    duration: input.duration,
    type: { ...legacy.type, id: input.typeId, name: input.typeName ?? legacy.type?.name ?? "" },
    type_id: input.typeId,
    type_name: input.typeName,
    status: input.status,
    booking_status: input.status,
    auto_confirmed: input.status === undefined ? undefined : input.status === "confirmed",
    venue_title: input.venueTitle,
  };
}

/**
 * Return-URL data for a booking confirmed in-page by `/submitBooking`, whose
 * response carries the booking exactly as legacy DMN sent it.
 */
export function returnRedirectFromSubmit(
  data: BookingConfirmationData,
  duration: number | null | undefined,
): ReturnRedirectData {
  const { booking, bookingStatus, venue } = data;
  const payload = buildPayload(legacySummaryOf(booking), {
    bookingId: booking._id,
    reference: booking.reference,
    venueId: booking.venue_id,
    date: booking.date,
    time: booking.time,
    numPeople: booking.num_people,
    duration: duration ?? undefined,
    typeId: booking.type.id,
    typeName: booking.type.name,
    status: bookingStatus,
    venueTitle: venue.title,
  });
  const guest = legacyCallbackGuestParser.safeParse(booking);
  return {
    payload,
    postBooking: {
      ...payload,
      ...(guest.success ? guest.data : {}),
      bookingStatus,
      venue,
    },
  };
}

/**
 * Return-URL data for a booking confirmed on `/confirm` after a payment redirect.
 *
 * The legacy fields come from the booking Collins verified the payment against.
 * Where Collins could not supply them, the venue, booking type and duration
 * fall back to the server's cached pending booking, and the guest's details
 * always come from the guest's own session.
 */
export function returnRedirectFromCheckDeposit(booking: Booking): ReturnRedirectData {
  const legacy = legacySummaryOf(booking.legacyBooking);
  const payload = buildPayload(legacy, {
    bookingId: booking.bookingId,
    reference: booking.bookingRef,
    venueId: legacy.venue_id ?? booking.venueId ?? "",
    date: typeof booking.date === "string" ? booking.date : "",
    time: booking.time ?? "",
    numPeople: booking.guestCount,
    duration: booking.duration,
    typeId: legacy.type?.id ?? booking.typeId ?? "",
    typeName: booking.bookingTypeName,
    status: booking.status,
    venueTitle: booking.venue.title,
  });
  return {
    payload,
    postBooking: {
      ...payload,
      first_name: booking.bookingDetails?.first_name ?? undefined,
      last_name: booking.bookingDetails?.last_name ?? undefined,
      email: booking.bookingDetails?.email ?? undefined,
      notes: booking.bookingDetails?.notes,
      bookingStatus: booking.status,
    },
  };
}

/**
 * Flatten the payload into GET query parameters as legacy DMN did: objects and
 * arrays (`type`, `deposits`, `offer`, `packages`) as JSON, absent values dropped.
 */
export function toReturnQueryParams(payload: ReturnRedirectPayload): Record<string, string> {
  const params: Record<string, string> = {};
  for (const [key, value] of Object.entries(payload)) {
    if (value === undefined || value === null || value === "") continue;
    params[key] = typeof value === "object" ? JSON.stringify(value) : String(value);
  }
  return params;
}

/** The partner return target in the shape `/bindReturnUrl` binds to the guest's session. */
export interface PartnerReturnBinding {
  return_url: string;
  return_method?: "get" | "post";
}

/**
 * The `return_url` and `return_method` to bind before the guest is sent to
 * pay, so `/confirm` redirects only where the journey began. Null when the
 * journey has no return target the server would accept.
 */
export function partnerReturnBinding(
  returnUrl: string | undefined,
  returnMethod: string | undefined,
): PartnerReturnBinding | null {
  const url = partnerReturnUrlParser.safeParse(returnUrl);
  if (!url.success) return null;
  const method = partnerReturnMethodParser.safeParse(returnMethod);
  return { return_url: url.data, ...(method.success ? { return_method: method.data } : {}) };
}
